import { ReactNode } from 'react'
import { Colors } from 'src/components/theme'
import chroma from 'chroma-js'
import { FC, useMemo } from 'react'
import { Typography } from '@mui/material'
import { DedupeResults } from 'src/views/map-explorer/sidebar/related-results/types'
import { POI_CATEGORIES_MAP } from 'src/constants/poi-categories'
import { getCategoryColorFromCategory, getCategoryFromNaicsCode } from 'src/constants/mapbox'
import { buildLocationResults } from 'src/views/map-explorer/sidebar/panels/results/search-result-items'
import { ColoredItem } from 'src/components/colored-item'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'
import { LocationLogo } from 'src/components/insights/location-visits/stats/location-logo'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'

type TitleProps = {
  children?: ReactNode
}
const Title: FC<TitleProps> = ({ children }) => {
  return (
    <Typography variant="textmd" color={Colors.neutral700}>
      {children}
    </Typography>
  )
}

type PlacesResultsListProps = {
  selectedEntityId?: string
  results?: DedupeResults
}
export const PlacesResultsList: FC<PlacesResultsListProps> = ({ selectedEntityId, results }) => {
  const { selectLocation } = useMapExplorerNavigation()

  const handleSelectLocation = async (id: string) => {
    if (selectedEntityId === id) {
      return
    }

    await selectLocation(id)
  }

  const locationsByCategory = useMemo(() => {
    if (!results || (results.shoppingCenters.length === 0 && results.locations.length === 0)) {
      return []
    }
    const categories = new Set(results.locations.map((l) => getCategoryFromNaicsCode(l.meta.naicsCode)?.category))
    const ordered = Array.from(POI_CATEGORIES_MAP.entries())
    const categoryOrder = Object.fromEntries(ordered.map(([key], index) => [key, index]))

    return Array.from(categories)
      .sort((a, b) => {
        const aCategory = a.toUpperCase()
        const bCategory = b.toUpperCase()
        const aIndex = categoryOrder[aCategory] !== undefined ? categoryOrder[aCategory] : Infinity
        const bIndex = categoryOrder[bCategory] !== undefined ? categoryOrder[bCategory] : Infinity
        return aIndex - bIndex || a.localeCompare(b)
      })
      .map((category) => {
        return {
          category,
          locations: buildLocationResults(
            results.locations.filter((l) => getCategoryFromNaicsCode(l.meta.naicsCode)?.category === category),
            { sort: true, iconSize: 12, brandIconSize: 14, dynamicIconColor: true }
          ),
        }
      })
  }, [results])

  if (
    !results ||
    (locationsByCategory.length === 0 && (!results?.shoppingCenters || results.shoppingCenters.length === 0))
  ) {
    return null
  }

  return (
    <FlexColumn gap={12}>
      {results.shoppingCenters.length > 0 && (
        <FlexColumn gap={12}>
          <Title>{`Shopping Centers (${results.shoppingCenters.length})`}</Title>
          {results?.shoppingCenters.map((shoppingCenter) => (
            <ColoredItem
              key={shoppingCenter.id}
              color={Colors.primaryBlue500}
              highlight={selectedEntityId === shoppingCenter.id}
              onClick={
                selectedEntityId === shoppingCenter.id ? undefined : () => handleSelectLocation(shoppingCenter.id)
              }>
              <FlexRow align="center" gap={8}>
                <LocationLogo
                  location={{
                    name: shoppingCenter.title,
                    naicsCode: shoppingCenter.meta.naicsCode ?? '',
                  }}
                  size={14}
                />
                <Typography variant="textmdm" color={Colors.neutral700} noWrap>
                  {shoppingCenter.title}
                </Typography>
              </FlexRow>
            </ColoredItem>
          ))}
        </FlexColumn>
      )}
      {locationsByCategory.map((category) => (
        <FlexColumn gap={12} key={category.category}>
          <Title>{`${category.category} (${category.locations.length})`}</Title>
          {category.locations.map((location) => (
            <ColoredItem
              key={location.id}
              color={chroma(getCategoryColorFromCategory(category.category)).hex()}
              highlight={selectedEntityId === location.id}
              onClick={selectedEntityId === location.id ? undefined : () => handleSelectLocation(location.id)}>
              <FlexRow align="center" gap={8}>
                {location.icon}
                <Typography variant="textmdm" color={Colors.neutral700} noWrap>
                  {location.title}
                </Typography>
              </FlexRow>
            </ColoredItem>
          ))}
        </FlexColumn>
      ))}
    </FlexColumn>
  )
}
